import {createInjectionState} from '@vueuse/core'
import {onBeforeUnmount, ref, unref, watch, type CSSProperties, type MaybeRef} from 'vue'
import {IPointerDragPreviewData} from '@toolify/client/src/modules/ui/types/IPointerDragPreviewData'
import {isTextEntryElement} from '@toolify/client/src/modules/keyboard/functions/textEntryFocus'

const POINTER_DRAG_IGNORE_SELECTOR = '[data-pointer-drag-ignore]'

// Inputs, textareas, selects and contenteditable keep native selection. Anything else that must
// not start a move (a slider, a custom selection) opts out with data-pointer-drag-ignore.
function shouldIgnorePointerDrag(target: EventTarget | null): boolean {
  if(isTextEntryElement(target)) {
    return true
  }
  return target instanceof Element && Boolean(target.closest(POINTER_DRAG_IGNORE_SELECTOR))
}

const POINTER_DRAGGING_CLASS = 'is-pointer-dragging'
const DROP_LINE_BOX_ATTRIBUTE = 'data-drop-line-box'
const DRAG_THRESHOLD_PX = 4
const EDGE_RATIO = 0.25

interface IRegisteredDropTarget {
  id: symbol
  kind: 'target'
  element: HTMLElement
  accepts: (payload: unknown) => boolean
  onDrop: (payload: unknown) => void
}

interface IRegisteredDropList {
  id: symbol
  kind: 'list'
  element: HTMLElement
  orientation: 'vertical' | 'horizontal'
  itemIds: MaybeRef<string[]>
  accepts: (payload: unknown) => boolean
  canDropOnItem?: (payload: unknown, itemId: string) => boolean
  onInsert: (payload: unknown, index: number) => void
  onDropOnItem?: (payload: unknown, itemId: string) => void
}

type IRegisteredDrop = IRegisteredDropTarget | IRegisteredDropList

function getInsertLineRect(element: HTMLElement): DOMRect {
  const marked = element.querySelector<HTMLElement>(`[${DROP_LINE_BOX_ATTRIBUTE}]`)
  if(marked) {
    return marked.getBoundingClientRect()
  }
  const child = element.firstElementChild
  if(child instanceof HTMLElement) {
    return child.getBoundingClientRect()
  }
  return element.getBoundingClientRect()
}

const [useProvidePointerDragDrop, usePointerDragDrop] = createInjectionState(() => {
  const isDragging = ref(false)
  const payload = ref<unknown>(null)
  const preview = ref<IPointerDragPreviewData | null>(null)
  const activeSourceId = ref<symbol | null>(null)
  const pointerX = ref(0)
  const pointerY = ref(0)
  const hoveredDropId = ref<symbol | null>(null)
  const insertIndex = ref<number | null>(null)
  const insertEdge = ref<'before' | 'after' | null>(null)
  const hoveredItemId = ref<string | null>(null)
  const insertLineStyle = ref<CSSProperties | null>(null)

  const drops = new Map<symbol, IRegisteredDrop>()
  let pointerDown: {x: number, y: number, payload: unknown, preview: IPointerDragPreviewData, sourceId: symbol} | null = null

  function registerDrop(drop: IRegisteredDrop) {
    drops.set(drop.id, drop)
    return () => {
      drops.delete(drop.id)
    }
  }

  function resetHover() {
    hoveredDropId.value = null
    insertIndex.value = null
    insertEdge.value = null
    hoveredItemId.value = null
    insertLineStyle.value = null
  }

  function endDrag() {
    isDragging.value = false
    payload.value = null
    preview.value = null
    activeSourceId.value = null
    pointerDown = null
    resetHover()
    window.removeEventListener('pointermove', onWindowPointerMove)
    window.removeEventListener('pointerup', onWindowPointerUp)
    window.removeEventListener('pointercancel', onWindowPointerUp)
  }

  function hitTest(clientX: number, clientY: number) {
    resetHover()
    if(!payload.value) {
      return
    }
    const matches: {drop: IRegisteredDrop, area: number}[] = []
    for(const drop of drops.values()) {
      if(!drop.accepts(payload.value)) {
        continue
      }
      const rect = drop.element.getBoundingClientRect()
      if(clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        continue
      }
      matches.push({
        drop,
        area: rect.width * rect.height,
      })
    }
    if(!matches.length) {
      return
    }
    matches.sort((a, b) => a.area - b.area)
    const {drop} = matches[0]
    hoveredDropId.value = drop.id
    if(drop.kind === 'target') {
      return
    }
    hitTestList(drop, clientX, clientY)
  }

  function hitTestList(drop: IRegisteredDropList, clientX: number, clientY: number) {
    const itemIds = unref(drop.itemIds)
    const itemElements = [...drop.element.querySelectorAll<HTMLElement>('[data-drop-item-id]')]
    const isVertical = drop.orientation === 'vertical'
    const axisPos = isVertical ? clientY : clientX
    const listRect = drop.element.getBoundingClientRect()

    if(!itemElements.length) {
      insertIndex.value = itemIds.length
      insertEdge.value = 'after'
      insertLineStyle.value = isVertical
        ? {
          top: `${listRect.top}px`,
          left: `${listRect.left}px`,
          width: `${listRect.width}px`,
          height: '2px',
        }
        : {
          top: `${listRect.top}px`,
          left: `${listRect.left}px`,
          width: '2px',
          height: `${listRect.height}px`,
        }
      return
    }

    for(const element of itemElements) {
      const itemId = element.dataset.dropItemId
      if(!itemId) {
        continue
      }
      const rect = element.getBoundingClientRect()
      const inItem = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
      if(!inItem) {
        continue
      }
      const along = isVertical
        ? (clientY - rect.top) / Math.max(rect.height, 1)
        : (clientX - rect.left) / Math.max(rect.width, 1)
      const canDropOn = drop.canDropOnItem?.(payload.value, itemId) === true
      if(canDropOn && along > EDGE_RATIO && along < (1 - EDGE_RATIO)) {
        hoveredItemId.value = itemId
        insertIndex.value = null
        insertEdge.value = null
        insertLineStyle.value = null
        return
      }
    }

    const ranked = itemElements
      .map(element => {
        const itemId = element.dataset.dropItemId
        const rect = element.getBoundingClientRect()
        const lineRect = getInsertLineRect(element)
        const index = itemId ? itemIds.indexOf(itemId) : -1
        const start = isVertical ? rect.top : rect.left
        const end = isVertical ? rect.bottom : rect.right
        return {
          rect,
          lineRect,
          index,
          mid: (start + end) / 2,
        }
      })
      .filter(item => item.index >= 0)
      .sort((a, b) => a.index - b.index)

    if(!ranked.length) {
      insertIndex.value = itemIds.length
      insertEdge.value = 'after'
      insertLineStyle.value = isVertical
        ? {
          top: `${listRect.bottom}px`,
          left: `${listRect.left}px`,
          width: `${listRect.width}px`,
          height: '2px',
        }
        : {
          top: `${listRect.top}px`,
          left: `${listRect.right}px`,
          width: '2px',
          height: `${listRect.height}px`,
        }
      return
    }

    let nextIndex = ranked[ranked.length - 1].index + 1
    let lineRect = ranked[ranked.length - 1].lineRect
    let isBefore = false
    for(const item of ranked) {
      if(axisPos < item.mid) {
        nextIndex = item.index
        lineRect = item.lineRect
        isBefore = true
        break
      }
      nextIndex = item.index + 1
      lineRect = item.lineRect
      isBefore = false
    }

    insertIndex.value = nextIndex
    insertEdge.value = isBefore ? 'before' : 'after'
    insertLineStyle.value = isVertical
      ? {
        top: `${isBefore ? lineRect.top : lineRect.bottom}px`,
        left: `${lineRect.left}px`,
        width: `${lineRect.width}px`,
        height: '2px',
      }
      : {
        top: `${lineRect.top}px`,
        left: `${isBefore ? lineRect.left : lineRect.right}px`,
        width: '2px',
        height: `${lineRect.height}px`,
      }
  }

  function commitDrop() {
    if(!payload.value || !hoveredDropId.value) {
      return
    }
    const drop = drops.get(hoveredDropId.value)
    if(!drop) {
      return
    }
    if(drop.kind === 'target') {
      drop.onDrop(payload.value)
      return
    }
    if(hoveredItemId.value && drop.onDropOnItem) {
      drop.onDropOnItem(payload.value, hoveredItemId.value)
      return
    }
    if(insertIndex.value !== null) {
      drop.onInsert(payload.value, insertIndex.value)
    }
  }

  function onWindowPointerMove(event: PointerEvent) {
    pointerX.value = event.clientX
    pointerY.value = event.clientY
    if(!isDragging.value && pointerDown) {
      const dx = event.clientX - pointerDown.x
      const dy = event.clientY - pointerDown.y
      if(Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return
      }
      isDragging.value = true
      payload.value = pointerDown.payload
      preview.value = pointerDown.preview
      activeSourceId.value = pointerDown.sourceId
    }
    if(isDragging.value) {
      hitTest(event.clientX, event.clientY)
    }
  }

  function swallowClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    window.removeEventListener('click', swallowClick, true)
  }

  function onWindowPointerUp() {
    const wasDragging = isDragging.value
    if(wasDragging) {
      commitDrop()
    }
    endDrag()
    if(!wasDragging) {
      return
    }
    window.addEventListener('click', swallowClick, true)
    window.setTimeout(() => window.removeEventListener('click', swallowClick, true), 0)
  }

  function beginPotentialDrag(
    event: PointerEvent,
    nextPayload: unknown,
    nextPreview: IPointerDragPreviewData,
    sourceId: symbol,
  ) {
    if(event.button !== 0 || shouldIgnorePointerDrag(event.target)) {
      return
    }
    pointerDown = {
      x: event.clientX,
      y: event.clientY,
      payload: nextPayload,
      preview: nextPreview,
      sourceId,
    }
    pointerX.value = event.clientX
    pointerY.value = event.clientY
    window.addEventListener('pointermove', onWindowPointerMove)
    window.addEventListener('pointerup', onWindowPointerUp)
    window.addEventListener('pointercancel', onWindowPointerUp)
  }

  watch(isDragging, (dragging) => {
    document.documentElement.classList.toggle(POINTER_DRAGGING_CLASS, dragging)
  })
  onBeforeUnmount(() => {
    document.documentElement.classList.remove(POINTER_DRAGGING_CLASS)
  })

  return {
    isDragging,
    payload,
    preview,
    pointerX,
    pointerY,
    hoveredDropId,
    insertIndex,
    insertEdge,
    hoveredItemId,
    insertLineStyle,
    activeSourceId,
    registerDrop,
    beginPotentialDrag,
  }
})

function requirePointerDragDrop() {
  const context = usePointerDragDrop()
  if(!context) {
    throw new Error('usePointerDragDrop must be used inside AppLayout')
  }
  return context
}

export {useProvidePointerDragDrop, usePointerDragDrop, requirePointerDragDrop}
